/**
 * AdminCopilotPanel — drawer laterale destro per AI Ops Co-pilot admin.
 *
 * UI features:
 *  - Drawer Sheet (apertura/chiusura controllata dal parent)
 *  - Header con title "AI Co-pilot" + bottoni "Nuova chat" / "History" / "X close"
 *  - Pannello messaggi conversazione con markdown rendering
 *  - Suggested prompts quando la chat è vuota (4 quick-start)
 *  - Input box con Send button + supporto Enter
 *  - Status messages durante tool calls (loader feedback)
 *  - History popover: lista ultime 50 sessioni cliccabili
 *
 * Sprint admin_ai_copilot S5 (28/05/26).
 * Override esplicito regola FE-via-Lovable: questo componente vive solo
 * in admin area, isolato dal recruiter FE, quindi gestito da Cursor.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Sparkles, Send, X, Plus, History, Loader2, AlertCircle, MessageSquare,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAdminCopilot, type CopilotMessage } from '@/hooks/admin/useAdminCopilot';

const SUGGESTED_PROMPTS = [
  'Trial in scadenza nei prossimi 7 giorni',
  'Snapshot piattaforma di oggi',
  'Cerca la company "Doona"',
  'Quante aziende sul piano starter?',
];

export interface AdminCopilotPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminCopilotPanel({ open, onOpenChange }: AdminCopilotPanelProps) {
  const {
    sessionId, messages, sending, loadingHistory, error,
    send, openSession, newChat,
    sessions, loadSessions, loadingSessions,
  } = useAdminCopilot();

  const [input, setInput] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Carica history quando si apre il popover
  useEffect(() => {
    if (historyOpen) loadSessions();
  }, [historyOpen, loadSessions]);

  const handleSend = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    if (!text) setInput('');
    await send(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] lg:w-[560px] p-0 flex flex-col bg-background"
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">AI Co-pilot</p>
            <p className="text-xs text-muted-foreground truncate">
              {sessionId ? `Session ${sessionId.slice(0, 8)}…` : 'Nuova chat'}
            </p>
          </div>

          <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" title="Cronologia">
                <History className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <SessionHistoryList
                sessions={sessions}
                loading={loadingSessions}
                activeSessionId={sessionId}
                onSelect={async (sid) => {
                  await openSession(sid);
                  setHistoryOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={newChat}
            title="Nuova chat"
            disabled={sending}
          >
            <Plus className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onOpenChange(false)}
            title="Chiudi"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loadingHistory && (
            <div className="flex items-center justify-center py-12 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin mr-2" />
              Caricamento cronologia…
            </div>
          )}

          {!loadingHistory && messages.length === 0 && (
            <EmptyState onPromptClick={(p) => handleSend(p)} />
          )}

          {messages.map((m) => (
            <MessageBubble key={m.messageId} message={m} />
          ))}

          {sending && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground pl-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Sto pensando…</span>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-xs">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Input box */}
        <div className="border-t border-border p-3 bg-card">
          <div className="relative">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Chiedi qualcosa al co-pilot... (Enter per inviare, Shift+Enter per andare a capo)"
              disabled={sending}
              rows={2}
              className="resize-none pr-12 text-sm"
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleSend()}
              disabled={sending || !input.trim()}
              className="absolute right-1 bottom-1 h-8 w-8"
              title="Invia"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground text-center">
            AI Co-pilot · sudo-only · cross-tenant read-only
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageBubble({ message }: { message: CopilotMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground',
          message.pending && 'opacity-50',
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <>
            {message.statusMessages && message.statusMessages.length > 0 && (
              <div className="mb-2 space-y-0.5">
                {message.statusMessages.map((s, i) => (
                  <p key={i} className="text-[10px] italic text-muted-foreground/80">
                    {s}
                  </p>
                ))}
              </div>
            )}
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-pre:my-1 prose-ul:my-1 prose-ol:my-1">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            </div>
            {message.tokenUsage && (
              <p className="mt-2 text-[10px] text-muted-foreground/70 font-mono">
                {message.tokenUsage.promptTokens}+{message.tokenUsage.completionTokens} tok
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onPromptClick }: { onPromptClick: (prompt: string) => void }) {
  return (
    <div className="text-center py-8 space-y-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
      <div>
        <p className="text-sm font-semibold">Ciao 👋</p>
        <p className="text-xs text-muted-foreground mt-1">
          Posso aiutarti con query cross-tenant su billing, trial,<br />
          subscriptions e KPI piattaforma.
        </p>
      </div>
      <div className="space-y-1.5">
        {SUGGESTED_PROMPTS.map((p) => (
          <button
            key={p}
            onClick={() => onPromptClick(p)}
            className="block w-full text-left text-xs px-3 py-2 rounded-md border border-border hover:border-primary hover:bg-accent/50 transition-colors"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function SessionHistoryList({
  sessions,
  loading,
  activeSessionId,
  onSelect,
}: {
  sessions: ReturnType<typeof useAdminCopilot>['sessions'];
  loading: boolean;
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
}) {
  return (
    <div className="max-h-[420px] overflow-y-auto">
      <div className="px-3 py-2 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Cronologia
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin mr-2" />
          Caricamento…
        </div>
      ) : sessions.length === 0 ? (
        <div className="py-8 px-4 text-center text-xs text-muted-foreground">
          Nessuna cronologia ancora.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {sessions.map((s) => (
            <li key={s.sessionId}>
              <button
                onClick={() => onSelect(s.sessionId)}
                className={cn(
                  'w-full text-left px-3 py-2.5 hover:bg-accent transition-colors flex items-start gap-2',
                  s.sessionId === activeSessionId && 'bg-accent/60',
                )}
              >
                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">
                    {s.title || '(senza titolo)'}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(s.lastMessageAt).toLocaleString('it-IT', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })} · {s.messageCount} msg · {s.totalTokens} tok
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
